'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ShoppingBag, Check, Plus, Minus } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/components/LocaleLink';
import { toast } from 'sonner';
import { useCartStore } from '@/stores/cart';
import { gtagEvent } from '@/components/GoogleAnalytics';
import type { Product } from '@/types';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';
import { usePriceFormatter } from '@/hooks/usePriceFormatter';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { convertToEUR } from '@/lib/price';

export interface SliderProduct {
  id: number;
  name: string;
  slug: string;
  reference: string;
  price: number;
  manufacturer_name?: string;
  category_name?: string;
  image: string | null;
  quantity?: number;
  allow_backorder?: boolean;
  campaign_price?: number;
  campaign_discount_type?: string;
  campaign_discount_value?: number;
  position?: string;
}

function AddButton({ product }: { product: SliderProduct }) {
  const addItem = useCartStore((s) => s.addItem);
  const { locale, currency, priceMarkup } = useLocale();
  const exchangeRate = useExchangeRate();
  const [added, setAdded] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const cartProduct: Product = {
      id: product.id,
      name: product.name,
      slug: product.slug,
      reference: product.reference,
      price: product.price,
      quantity: product.quantity ?? 1,
      active: true,
      images: product.image ? [{ id: 0, product_id: product.id, external_url: product.image, position: 0, cover: true }] : [],
      manufacturer_name: product.manufacturer_name,
      category_name: product.category_name,
      created_at: '',
      campaign_price: product.campaign_price,
      campaign_discount_type: product.campaign_discount_type,
      campaign_discount_value: product.campaign_discount_value,
    };

    addItem(cartProduct, quantity);
    toast.success(t(locale, 'cart.itemAdded'));
    const sekUnitPrice = product.campaign_price ?? product.price;
    const gtagUnitPrice = currency === 'EUR' ? convertToEUR(sekUnitPrice, exchangeRate, priceMarkup) : sekUnitPrice;
    gtagEvent('add_to_cart', {
      currency,
      value: gtagUnitPrice * quantity,
      items: [{ item_id: product.reference, item_name: product.name, price: gtagUnitPrice, quantity, ...(product.manufacturer_name && { item_brand: product.manufacturer_name }), ...(product.category_name && { item_category: product.category_name }) }],
    });

    setAdded(true);
    setQuantity(1);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex items-center justify-center border rounded-md bg-gray-50" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuantity(Math.max(1, quantity - 1)); }}
          className="h-8 w-9 flex items-center justify-center hover:bg-muted rounded-l-md transition-colors"
          aria-label={t(locale, 'cart.decreaseQty')}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="w-7 text-center text-sm font-medium">{quantity}</span>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuantity(quantity + 1); }}
          className="h-8 w-9 flex items-center justify-center hover:bg-muted rounded-r-md transition-colors"
          aria-label={t(locale, 'cart.increaseQty')}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
      <button
        onClick={handleAdd}
        className={`w-full py-1.5 rounded-lg text-xs font-semibold transition-colors ${
          added
            ? 'bg-green-600 text-white'
            : 'bg-primary text-primary-foreground hover:bg-primary/90'
        }`}
      >
        {added ? (
          <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {t(locale, 'product.added')}</span>
        ) : (
          t(locale, 'product.buy')
        )}
      </button>
    </div>
  );
}

export function ProductRecommendationSlider({ products }: { products: SliderProduct[] }) {
  const { locale } = useLocale();
  const fp = usePriceFormatter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>('a')?.offsetWidth || 180;
    const amount = cardWidth * 3;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  if (products.length === 0) return null;

  return (
    <div className="relative group/slider">
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-20 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors opacity-0 group-hover/slider:opacity-100"
          aria-label={t(locale, 'hero.prevSlide')}
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-20 w-10 h-10 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors opacity-0 group-hover/slider:opacity-100"
          aria-label={t(locale, 'hero.nextSlide')}
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      )}

      {canScrollLeft && (
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
      )}
      {canScrollRight && (
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
      )}

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 -mx-1 px-1"
      >
        {products.map((product) => (
          <Link
            key={product.id}
            href={`/produkt/${product.slug || product.id}`}
            className="group flex-shrink-0 w-[170px] sm:w-[180px] snap-start bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all duration-200 flex flex-col"
          >
            <div className="relative bg-gradient-to-br from-gray-50 to-white h-32 flex items-center justify-center p-2 overflow-hidden">
              {product.campaign_price != null && (
                <span className="absolute top-1.5 left-1.5 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-lg z-10">
                  {product.campaign_discount_type === 'percent' && product.campaign_discount_value
                    ? `-${product.campaign_discount_value}%`
                    : `-${Math.round((1 - product.campaign_price / product.price) * 100)}%`}
                </span>
              )}
              {product.image ? (
                <div className="relative w-[80%] h-[80%]">
                  <Image
                    src={product.image}
                    alt={product.name}
                    fill
                    sizes="180px"
                    className="object-contain group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                </div>
              ) : (
                <ShoppingBag className="h-8 w-8 text-gray-300" />
              )}
            </div>
            <div className="p-2.5 flex flex-col flex-1">
              {(product.manufacturer_name || product.category_name) && (
                <p className="text-[9px] text-gray-500 font-semibold uppercase tracking-wide">
                  {product.manufacturer_name || product.category_name}
                </p>
              )}
              <p className="text-xs font-semibold text-gray-700 mt-0.5 line-clamp-2 leading-snug flex-1">{product.name}</p>
              {product.position && !product.name.toLowerCase().includes(product.position.toLowerCase()) && (
                <p className="text-[10px] text-gray-400 mt-0.5 truncate">{product.position}</p>
              )}
              {product.campaign_price != null ? (
                <div className="mt-1 flex items-baseline gap-1.5">
                  <span className="text-sm font-extrabold text-red-600">{fp(product.campaign_price)}</span>
                  <span className="text-[10px] text-gray-400 line-through">{fp(Number(product.price))}</span>
                </div>
              ) : (
                <p className="text-sm font-extrabold mt-1">{fp(Number(product.price))}</p>
              )}
              <AddButton product={product} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
